import { useSelector } from 'react-redux';

import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import { NoPiiTestAsset } from '@core/context/types';
import { selectInput } from '@core/reducers/inputsSlice';
import { selectTestResultById } from '@core/reducers/monarchSlice';

type NoPiiVariant = 'noPiiZipOnly' | 'noPiiZipName' | 'control' | null;

type NoPiiVariantResult = {
  variant: NoPiiVariant;
  isNoPiiTestEnabled: boolean;
  startDateValues: number[] | null;
  startTime: number | null | undefined;
};

/**
 * Hook to check noPii test variant and whether it's enabled based on startTime
 */
export const useNoPiiVariant = (): NoPiiVariantResult => {
  const flags = useFeatureFlags();
  const noPiiFromMonarch = useSelector((state) => selectTestResultById(state, 'noPii'));
  const noPii = (flags?.noPii || noPiiFromMonarch || false) as NoPiiTestAsset | false;
  const startTime = useSelector((state) => selectInput(state, 'startTime'));

  if (!noPii || typeof noPii !== 'object') {
    return {
      variant: null,
      isNoPiiTestEnabled: false,
      startDateValues: null,
      startTime,
    };
  }

  const variantCandidate = (noPii as { variant?: string }).variant;
  let variant: NoPiiVariant = 'control';
  if (variantCandidate === 'noPiiZipOnly') {
    variant = 'noPiiZipOnly';
  } else if (variantCandidate === 'noPiiZipName') {
    variant = 'noPiiZipName';
  }
  const startDateValues = 'startDateValues' in noPii ? (noPii as any).startDateValues : null;

  // Normalize startTime and startDateValues to numbers for comparison
  const normalizedStartTime = startTime !== undefined && startTime !== null ? Number(startTime) : null;
  const normalizedStartDates = Array.isArray(startDateValues) ? startDateValues.map(Number) : null;

  // Check if normalizedStartTime is in the normalizedStartDates array to enable the test
  const isNoPiiTestEnabled =
    normalizedStartDates !== null && normalizedStartTime !== null && normalizedStartDates.includes(normalizedStartTime);

  return {
    variant,
    isNoPiiTestEnabled,
    startDateValues,
    startTime,
  };
};
